import { ButtonType, TextFieldWidget, TextInputType } from '@tableau/widgets';
import * as React from 'react';

import { LocalAuthConfiguration } from '~src/authentication/models/LocalAuthConfiguration';
import { IPasswordPolicy } from '~src/authentication/models/validation/IPasswordPolicy';

import { getAppStore } from '../../../app/AppStore';
import { setRootAdminUiState } from '../../../app/rootAdminUi/RootAdminUiStateActions';
import { siteUrlsSelector } from '../../../app/selectors/SiteUrlsSelectors';
import { SignInLogo } from '../../../app/spaces/Login/SignInLogo';
import { Messages } from '../../../codegen/Localize';
import { ButtonWithSpinner } from '../../../common/react/components/ButtonWithSpinner';
import { Location } from '../../../common/react/Location';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { wideField } from '../../../settings/general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { UserErrorCode } from '../../../ts/helpers/ErrorCodes';
import { Keys } from '../../../ts/helpers/Keys';
import { ValidationHelpers } from '../../../userManagement/helpers/ValidationHelpers';
import { InitializationFormStyles } from '../../styles/initialization/InitializationFormStyles';

export const INIT_FORM_TEST_ID = 'initialization-form';
export const USERNAME_TEXT_FIELD_TEST_ID = 'username-input-text-box';
export const DISPLAY_NAME_TEXT_FIELD_TEST_ID = 'display-name-input-text-box';
export const PASSWORD_TEXT_FIELD_TEST_ID = 'password-input-text-box';
export const CONFIRM_PASSWORD_TEXT_FIELD_TEST_ID = 'confirm-password-input-text-box';
export const SIGN_IN_BUTTON_TEST_ID = 'sign-in-button';
export const USERNAME_ERROR_TEST_ID = 'username-error';
export const PASSWORD_ERROR_TEST_ID = 'password-error';

const MAX_USERNAME_LENGTH: number = 255;
const MAX_DISPLAY_NAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

export interface InitializationFormState {
  userName: string;
  displayName: string;
  password: string;
  confirmPassword: string;
  errorText: string;
  busy: boolean;
}

export class InitializationForm extends React.Component<{}, InitializationFormState> {
  private server = getSessionManagementServiceInstance();
  private localAuthConfiguration: LocalAuthConfiguration = LocalAuthConfiguration.default();

  public constructor() {
    super({});
    this.state = {
      busy: false,
      userName: '',
      displayName: '',
      password: '',
      confirmPassword: '',
      errorText: ''
    };
  }

  public async componentDidMount() {
    this.localAuthConfiguration = await LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration();
  }

  private onUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, MAX_USERNAME_LENGTH);
    this.setState({ userName: newText });
  };

  private onDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, MAX_DISPLAY_NAME_LENGTH);
    this.setState({ displayName: newText });
  };

  private onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, MAX_PASSWORD_LENGTH);
    this.setState({ password: newText });
  };

  private onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, MAX_PASSWORD_LENGTH);
    this.setState({ confirmPassword: newText });
  };

  private onKeyDown = (e: any) => {
    if(e.keyCode === Keys.Enter && !this.isConfirmDisabled()) {
      this.confirm();
    }
  };

  private onSubmit = () => this.confirm();

  public render() {
    const isActiveDirectory = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        className={InitializationFormStyles.initializationForm}
        onKeyDown={this.onKeyDown}
        data-tb-test-id={INIT_FORM_TEST_ID}>
        <div>
          <SignInLogo/>
        </div>
        <header className={InitializationFormStyles.initializationLabel}>
          {Messages.serverSetup()}
        </header>
        <form onSubmit={this.onSubmit}
            className={InitializationFormStyles.initializationFormElement}>
          {this.getUserNameInput()}
          {!isActiveDirectory && this.getDisplayNameInput()}
          {this.getPasswordInput()}
          {!isActiveDirectory && this.getConfirmPasswordInput()}
        </form>
        {this.createErrorMessages()}
        <div className={InitializationFormStyles.initializationButton}>
          <ButtonWithSpinner
            buttonType={ButtonType.Primary}
            clicked={this.onSubmit}
            showSpinner={this.state.busy}
            testId={SIGN_IN_BUTTON_TEST_ID}
            disabled={this.isConfirmDisabled()}
            title={Messages.addAdministratorAccount()}/>
        </div>
      </div>
    );
  }

  private isConfirmDisabled(): boolean {
    if (this.state.busy) {
      return true;
    }

    // Active directory
    if (ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
      return ValidationHelpers.hasInvalidUsername(this.state.userName) || this.state.password.length === 0 ||
        this.state.userName.length === 0;
    }
    // Local authentication
    else {
      return !ValidationHelpers.isLocalUserInputsValid(
        this.state.userName,
        this.state.displayName,
        this.state.password,
        this.state.confirmPassword,
        true,
        !this.getFailedPasswordPolicies().length
      );
    }
  }

  private getUserNameInput(): JSX.Element {
    return (
      <TextFieldWidget
        kind='line'
        label={Messages.username()}
        onChange={this.onUserNameChange}
        testId={USERNAME_TEXT_FIELD_TEST_ID}
        className={wideField + ' ' + InitializationFormStyles.initializationInput}
        disabled={this.state.busy}
      />
    );
  }

  private getDisplayNameInput(): JSX.Element {
    return (
      <TextFieldWidget
        kind='line'
        label={Messages.displayName()}
        onChange={this.onDisplayNameChange}
        testId={DISPLAY_NAME_TEXT_FIELD_TEST_ID}
        className={wideField + ' ' + InitializationFormStyles.initializationInput}
        disabled={this.state.busy}
      />
    );
  }

  private getPasswordInput(): JSX.Element {
    return (
      <TextFieldWidget
        kind='line'
        label={Messages.password()}
        onChange={this.onPasswordChange}
        type={TextInputType.Password}
        testId={PASSWORD_TEXT_FIELD_TEST_ID}
        className={wideField + ' ' + InitializationFormStyles.initializationInput}
        disabled={this.state.busy}
      />
    );
  }

  private getConfirmPasswordInput(): JSX.Element {
    return (
      <TextFieldWidget
        kind='line'
        label={Messages.confirmPassword()}
        onChange={this.onConfirmPasswordChange}
        type={TextInputType.Password}
        testId={CONFIRM_PASSWORD_TEXT_FIELD_TEST_ID}
        className={wideField + ' ' + InitializationFormStyles.initializationInput}
        disabled={this.state.busy}
      />
    );
  }

  private createErrorMessages() {
    if (!this.state.busy) {
      return(<div>
              <div className={InitializationFormStyles.errorText} role="alert">{this.state.errorText}</div>
              {this.getUsernameError()}
              {this.getPasswordMatchError()}
              {ValidationHelpers.createPasswordPolicyErrorMessageSectionFor(this.getFailedPasswordPolicies())}
            </div>);
    }
  }

  public confirm() {
    this.setState({busy: true});
    this.server.initialize(this.state.userName, this.state.password, this.state.displayName)
      .then(() => {
        getAppStore().dispatch(setRootAdminUiState('default'));
        const urls = siteUrlsSelector(getAppStore().getState());
        Location.instance.setUrl(urls.contentRootUrl());
      })
      .catch((errorCode: number) => {
        this.setState({busy: false});
        switch(errorCode) {
          case UserErrorCode.UserInsufficientLicenses:
            this.setState({errorText: Messages.signInFailedUnlicensedServer()});
            break;
          case UserErrorCode.UserPasswordNotRespectingPolicies:
            this.setState({errorText: Messages.errorUserPasswordNotRespectingPolicies()});
            break;
          default:
            this.setState({errorText: Messages.initializationFailed()});
            break;
        }
      });
  }

  private getFailedPasswordPolicies(): IPasswordPolicy[] {
    return this.localAuthConfiguration.performPasswordValidation(this.state.password);
  }

  private getUsernameError(): JSX.Element{
    if (this.hasInvalidUsername()) {
      return(<div className={InitializationFormStyles.errorText + ' usernameError'}
                  role="alert"
                  data-tb-test-id={USERNAME_ERROR_TEST_ID}>
              {Messages.usernameInvalid()}
              <div>{ValidationHelpers.INVALID_USERNAME_CHARS_STR}</div>
            </div>);
    }
    return (null);
  }

  public hasInvalidUsername(): boolean {
    return ValidationHelpers.hasInvalidUsername(this.state.userName);
  }

  private getPasswordMatchError(): JSX.Element{
    if (this.hasNonMatchingPasswords()) {
      return (<div className={InitializationFormStyles.errorText + ' passwordMatchingError'}
                   role="alert"
                   data-tb-test-id={PASSWORD_ERROR_TEST_ID}>
        {Messages.passwordsMustMatch()}</div>);
    }
    return (null);
  }

  private hasNonMatchingPasswords(): boolean {
    return ValidationHelpers.hasNonMatchingPasswords(
      this.state.password,
      this.state.confirmPassword);
  }
}